/**
 * Recommend over the room's data channel (finding 11): the coach's tap goes
 * to the player at once, beside sendLiveRecommendation, so the banner is up
 * within a second instead of when the callable and the player's listener
 * have caught up (8.8 s on staging). The server's record replaces it when it
 * comes; one that never comes (the send failed, or was refused) goes after
 * RECOMMEND_WIRE_TTL_MS, or at once on the coach's "off". Pure, so tested.
 */
import type { RecommendOfferKey } from './recommendOffers.ts';

export const RECOMMEND_WIRE_TTL_MS = 30_000;

export interface RecommendWire { t: 'rec'; on: boolean; offer: RecommendOfferKey; note: string; priceCents: number; sentAt: string }

export function isRecommendWire(data: unknown): data is RecommendWire {
  const d = data as Partial<RecommendWire> | null;
  return !!d && typeof d === 'object' && d.t === 'rec' && typeof d.on === 'boolean'
    && (d.offer === 'single' || d.offer === 'membership' || d.offer === 'program')
    && typeof d.priceCents === 'number' && typeof d.sentAt === 'string';
}

/**
 * The player's recommendations: the server's, then any that came over the
 * channel and are not on the server yet, as sent but still pending (no
 * offering, so not yet payable).
 */
export function withWireRecommendations<R extends { offer: RecommendOfferKey }>(
  server: R[],
  wire: RecommendWire[],
  nowMs: number,
  pendingOf: (w: RecommendWire) => R,
): Array<R & { pending?: boolean }> {
  const latest = new Map<RecommendOfferKey, RecommendWire>();
  for (const w of wire) {
    const was = latest.get(w.offer);
    if (!was || was.sentAt <= w.sentAt) latest.set(w.offer, w);
  }
  const extra = [...latest.values()]
    .filter((w) => w.on && nowMs - new Date(w.sentAt).getTime() < RECOMMEND_WIRE_TTL_MS)
    .filter((w) => !server.some((r) => r.offer === w.offer))
    .map((w) => ({ ...pendingOf(w), pending: true }));
  return [...server, ...extra];
}
